<template>
  <div class="course-form">

    <div class="page-header">
      <div>
        <h5 class="page-title">{{ isEdit ? 'Edit Course' : 'New Course' }}</h5>
        <p class="page-sub">{{ isEdit ? 'Update course details' : 'Create a new training course' }}</p>
      </div>
      <button type="button" @click="$router.go(-1)" class="btn-outline">
        <font-awesome-icon icon="arrow-left" /> Back
      </button>
    </div>

    <div v-if="loading" class="full-loader"><div class="spinner"></div></div>

    <template v-else>
      <form @submit.prevent="submit">

        <!-- Basic Info -->
        <div class="form-card mb-4">
          <div class="card-section-title">Basic Information</div>
          <div class="field-grid">
            <div class="field-group field-group--full">
              <label class="field-label">Course Title <span class="req">*</span></label>
              <input v-model="form.title" type="text" class="field-input" placeholder="e.g. Health & Safety Induction" required />
            </div>
            <div class="field-group">
              <label class="field-label">Course Type <span class="req">*</span></label>
              <select v-model="form.course_type" class="field-input" required>
                <option value="">Select type</option>
                <option value="mandatory">Mandatory</option>
                <option value="recommended">Recommended</option>
                <option value="optional">Optional</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Category</label>
              <select v-model="selectedParentId" class="field-input" @change="onParentChange">
                <option value="">No category</option>
                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div v-if="selectedParentChildren.length" class="field-group">
              <label class="field-label">Sub-category</label>
              <select v-model="form.category_id" class="field-input">
                <option value="">None (use parent)</option>
                <option v-for="ch in selectedParentChildren" :key="ch.id" :value="ch.id">{{ ch.name }}</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Estimated Duration (minutes)</label>
              <input v-model.number="form.est_minutes" type="number" min="1" class="field-input" placeholder="e.g. 60" />
            </div>
            <div class="field-group">
              <label class="field-label">Language</label>
              <input v-model="form.language" type="text" class="field-input" placeholder="e.g. English" />
            </div>
            <div class="field-group field-group--full">
              <label class="field-label">Description</label>
              <textarea v-model="form.description" class="field-input field-textarea" rows="4" placeholder="Describe what learners will gain from this course…"></textarea>
            </div>
            <div class="field-group field-group--full">
              <label class="field-label">Learning Objectives</label>
              <textarea v-model="form.objectives" class="field-input field-textarea" rows="3" placeholder="List the key learning objectives…"></textarea>
            </div>
          </div>
        </div>

        <!-- Completion & Assessment -->
        <div class="form-card mb-4">
          <div class="card-section-title">Completion &amp; Assessment</div>
          <div class="field-grid">
            <div class="field-group">
              <label class="field-label">Completion Rule</label>
              <select v-model="form.completion_rule" class="field-input">
                <option value="all_lessons">All Lessons</option>
                <option value="assessment">Assessment Only</option>
                <option value="both">Lessons + Assessment</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Pass Mark (%)</label>
              <input v-model.number="form.pass_mark_pct" type="number" min="0" max="100" class="field-input" placeholder="e.g. 75" />
            </div>
            <div class="field-group">
              <label class="field-label">Default Due (days from enrolment)</label>
              <input v-model.number="form.default_due_days" type="number" min="1" class="field-input" placeholder="e.g. 30" />
            </div>
            <div class="field-group">
              <label class="field-label">Validity (months)</label>
              <input v-model.number="form.validity_months" type="number" min="1" class="field-input" placeholder="Leave blank = no expiry" />
            </div>
            <div class="field-group">
              <label class="field-label">Recertification Reminder (days before expiry)</label>
              <input v-model.number="form.recert_lead_days" type="number" min="1" class="field-input" placeholder="e.g. 30" />
            </div>
            <div class="field-group checkbox-group">
              <label class="check-label">
                <input type="checkbox" v-model="form.sequential" :true-value="1" :false-value="0" class="check-input" />
                <span>Lessons must be completed in order</span>
              </label>
              <label class="check-label">
                <input type="checkbox" v-model="form.requires_approval" :true-value="1" :false-value="0" class="check-input" />
                <span>Requires manager approval to enrol</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Error banner -->
        <div v-if="errorMsg" class="error-banner">
          <font-awesome-icon icon="exclamation-circle" />
          {{ errorMsg }}
        </div>

        <div class="form-footer">
          <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
          <button type="submit" class="btn-primary" :disabled="saving">
            <span v-if="saving" class="spinner-sm"></span>
            <font-awesome-icon v-else icon="check" />
            {{ saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Course') }}
          </button>
        </div>

      </form>

      <!-- ── Curriculum Builder (edit mode only) ──────────────────────────── -->
      <div v-if="isEdit" class="form-card mt-4">
        <div class="curriculum-header">
          <div class="card-section-title mb-0">Course Content</div>
          <div class="structure-toggle">
            <button type="button" :class="['struct-btn', { active: curriculum.mode === 'flat' }]" @click="curriculum.mode = 'flat'">
              No Sections
            </button>
            <button type="button" :class="['struct-btn', { active: curriculum.mode === 'sectioned' }]" @click="curriculum.mode = 'sectioned'">
              With Sections
            </button>
          </div>
        </div>

        <div v-if="curriculum.loading" class="curriculum-loading"><div class="spinner"></div></div>

        <!-- FLAT MODE -->
        <template v-else-if="curriculum.mode === 'flat'">
          <div v-if="!curriculum.flatLessons.length" class="curriculum-empty">
            <font-awesome-icon icon="file-video" class="empty-icon" />
            <span>No lessons yet. Click "Add Lesson" to get started.</span>
          </div>
          <div v-for="(lesson, i) in curriculum.flatLessons" :key="lesson.id" class="lesson-row">
            <span class="lesson-pos">{{ i + 1 }}</span>
            <span :class="['type-badge', 'type-' + lesson.lesson_type]">{{ lessonTypeLabel(lesson.lesson_type) }}</span>
            <span class="lesson-title-cell">{{ lesson.title }}</span>
            <div class="row-actions">
              <button type="button" class="row-btn" title="Edit" @click="openLessonModal(null, lesson)">
                <font-awesome-icon icon="pen" />
              </button>
              <button type="button" class="row-btn row-btn--danger" title="Delete" @click="showDeleteLesson(lesson, null)">
                <font-awesome-icon icon="trash" />
              </button>
            </div>
          </div>
          <button type="button" class="btn-add-item" @click="openLessonModal(null)">
            <font-awesome-icon icon="plus" /> Add Lesson
          </button>
        </template>

        <!-- SECTIONED MODE -->
        <template v-else>
          <div v-if="!curriculum.sections.length" class="curriculum-empty">
            <font-awesome-icon icon="layer-group" class="empty-icon" />
            <span>No sections yet. Click "Add Section" to structure your course.</span>
          </div>

          <div v-for="(section, si) in curriculum.sections" :key="section.id" class="section-block">
            <div class="section-header">
              <span class="section-num">Section {{ si + 1 }}</span>
              <input
                v-if="section._editing"
                v-model="section._titleDraft"
                class="section-title-input"
                @keyup.enter="saveSection(section)"
                @blur="saveSection(section)"
                ref="sectionTitleInputs"
              />
              <span v-else class="section-title-text" @click="editSectionTitle(section)">{{ section.title }}</span>
              <div class="section-header-actions">
                <button type="button" class="row-btn" title="Rename" @click="editSectionTitle(section)">
                  <font-awesome-icon icon="pen" />
                </button>
                <button type="button" class="row-btn row-btn--danger" title="Delete section" @click="showDeleteSection(section)">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </div>

            <div class="section-body">
              <div v-if="!section.lessons || !section.lessons.length" class="section-empty">
                No lessons in this section yet.
              </div>
              <div v-for="(lesson, li) in section.lessons" :key="lesson.id" class="lesson-row lesson-row--indented">
                <span class="lesson-pos">{{ li + 1 }}</span>
                <span :class="['type-badge', 'type-' + lesson.lesson_type]">{{ lessonTypeLabel(lesson.lesson_type) }}</span>
                <span class="lesson-title-cell">{{ lesson.title }}</span>
                <div class="row-actions">
                  <button type="button" class="row-btn" title="Edit" @click="openLessonModal(section.id, lesson)">
                    <font-awesome-icon icon="pen" />
                  </button>
                  <button type="button" class="row-btn row-btn--danger" title="Delete" @click="showDeleteLesson(lesson, section)">
                    <font-awesome-icon icon="trash" />
                  </button>
                </div>
              </div>
              <button type="button" class="btn-add-item btn-add-item--sm" @click="openLessonModal(section.id)">
                <font-awesome-icon icon="plus" /> Add Lesson
              </button>
            </div>
          </div>

          <button type="button" class="btn-add-item" @click="addSection" :disabled="sectionForm.saving">
            <span v-if="sectionForm.saving" class="spinner-sm spinner-sm--dark"></span>
            <font-awesome-icon v-else icon="plus" /> Add Section
          </button>
        </template>
      </div>

      <!-- Hint on create mode -->
      <div v-if="!isEdit" class="form-card mt-4 curriculum-hint">
        <font-awesome-icon icon="info-circle" />
        After saving the course, you'll be taken to the edit page where you can add lessons and content.
      </div>

    </template>

    <!-- ── Lesson Modal ────────────────────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="lessonModal.show" class="modal-backdrop" @click.self="lessonModal.show = false">
        <div class="modal-box">
          <div class="modal-head">
            <span class="modal-title">{{ lessonModal.editLesson ? 'Edit Lesson' : 'Add Lesson' }}</span>
            <button type="button" class="modal-close" @click="lessonModal.show = false">
              <font-awesome-icon icon="times" />
            </button>
          </div>
          <div class="modal-body-inner">
            <div class="field-group mb-3">
              <label class="field-label">Lesson Title <span class="req">*</span></label>
              <input v-model="lessonModal.form.title" type="text" class="field-input" placeholder="e.g. Introduction to Safety" />
            </div>
            <div class="field-group mb-3">
              <label class="field-label">Content Type <span class="req">*</span></label>
              <select v-model="lessonModal.form.lesson_type" class="field-input">
                <option value="video">Video (URL)</option>
                <option value="pdf">PDF (URL)</option>
                <option value="audio">Audio (URL)</option>
                <option value="link">Web Link</option>
                <option value="text">Text / Article</option>
                <option value="doc">Document (URL)</option>
              </select>
            </div>
            <div v-if="lessonModal.form.lesson_type !== 'text'" class="field-group mb-3">
              <label class="field-label">URL</label>
              <input v-model="lessonModal.form.external_url" type="text" class="field-input" :placeholder="lessonModal.form.lesson_type === 'video' ? 'https://youtube.com/watch?v=…' : 'https://…'" />
            </div>
            <div class="field-grid-2 mb-3">
              <div class="field-group">
                <label class="field-label">Duration (minutes)</label>
                <input v-model.number="lessonModal.form.duration_minutes" type="number" min="0" class="field-input" placeholder="e.g. 15" />
              </div>
              <div class="field-group checkbox-group" style="justify-content:flex-end">
                <label class="check-label">
                  <input type="checkbox" v-model="lessonModal.form.is_mandatory" :true-value="1" :false-value="0" class="check-input" />
                  <span>Mandatory lesson</span>
                </label>
              </div>
            </div>
            <div v-if="lessonModal.errorMsg" class="error-banner">
              <font-awesome-icon icon="exclamation-circle" /> {{ lessonModal.errorMsg }}
            </div>
          </div>
          <div class="modal-foot">
            <button type="button" class="btn-outline" @click="lessonModal.show = false">Cancel</button>
            <button type="button" class="btn-primary" @click="saveLessonModal" :disabled="lessonModal.saving">
              <span v-if="lessonModal.saving" class="spinner-sm"></span>
              <font-awesome-icon v-else icon="check" />
              {{ lessonModal.editLesson ? 'Save Changes' : 'Add Lesson' }}
            </button>
          </div>
        </div>
      </div>
    </teleport>

    <!-- ── Curriculum Confirm Modal ───────────────────────────────────────── -->
    <teleport to="body">
      <div v-if="currConfirm.show" class="modal-backdrop" @click.self="currConfirm.show = false">
        <div class="confirm-box">
          <div class="confirm-icon-wrap">
            <font-awesome-icon icon="trash" />
          </div>
          <h6 class="confirm-title">{{ currConfirm.title }}</h6>
          <p class="confirm-msg">{{ currConfirm.message }}</p>
          <div class="confirm-actions">
            <button type="button" class="btn-outline" @click="currConfirm.show = false">Cancel</button>
            <button type="button" class="btn-confirm-delete" @click="handleCurrConfirm" :disabled="currConfirm.loading">
              <span v-if="currConfirm.loading" class="spinner-sm"></span>
              Delete
            </button>
          </div>
        </div>
      </div>
    </teleport>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'CourseForm',
  setup() { return { toast: useToast() }; },
  data() {
    return {
      loading: false,
      saving: false,
      errorMsg: '',
      categories: [],
      selectedParentId: '',
      form: {
        title: '',
        course_type: '',
        category_id: '',
        description: '',
        objectives: '',
        est_minutes: '',
        language: '',
        completion_rule: 'all_lessons',
        pass_mark_pct: '',
        default_due_days: '',
        validity_months: '',
        recert_lead_days: '',
        sequential: 0,
        requires_approval: 0,
      },
      curriculum: {
        mode: 'flat',
        sections: [],
        flatLessons: [],
        loading: false,
      },
      sectionForm: { saving: false },
      lessonModal: {
        show: false,
        sectionId: null,
        editLesson: null,
        form: { title: '', lesson_type: 'video', external_url: '', is_mandatory: 1, duration_minutes: '' },
        saving: false,
        errorMsg: '',
      },
      currConfirm: {
        show: false,
        title: '',
        message: '',
        loading: false,
        onOk: null,
      },
    };
  },
  computed: {
    isEdit() { return !!this.$route.params.id; },
    courseId() { return this.$route.params.id || null; },
    selectedParentChildren() {
      if (!this.selectedParentId) return [];
      const parent = this.categories.find(c => c.id === this.selectedParentId);
      return parent?.children ?? [];
    },
  },
  async created() {
    await this.loadCategories();
    if (this.isEdit) this.loadCourse();
  },
  methods: {
    async loadCategories() {
      try {
        const { data } = await axios.get('/training/categories');
        this.categories = data.data ?? [];
      } catch { /* silent */ }
    },

    async loadCourse() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/training/courses/${this.courseId}`);
        const c = data.data;
        Object.keys(this.form).forEach(k => {
          if (c[k] !== undefined && c[k] !== null) {
            this.form[k] = typeof c[k] === 'boolean' ? (c[k] ? 1 : 0) : c[k];
          }
        });
        // Restore parent/sub-category
        if (c.category_id) {
          const isParent = this.categories.some(cat => cat.id === c.category_id);
          if (isParent) {
            this.selectedParentId = c.category_id;
          } else {
            const parent = this.categories.find(cat => cat.children?.some(ch => ch.id === c.category_id));
            if (parent) this.selectedParentId = parent.id;
          }
        }
        // Build curriculum
        this.buildCurriculum(c);
      } catch { this.toast.error('Failed to load course.'); }
      finally { this.loading = false; }
    },

    buildCurriculum(c) {
      const sections = (c.sections || []).map(s => ({
        ...s,
        lessons: s.lessons || [],
        _editing: false,
        _titleDraft: s.title,
      }));
      this.curriculum.sections = sections;
      this.curriculum.flatLessons = c.flat_lessons || [];
      this.curriculum.mode = sections.length > 0 ? 'sectioned' : 'flat';
    },

    onParentChange() {
      this.form.category_id = this.selectedParentId;
    },

    async submit() {
      this.errorMsg = '';
      this.saving = true;
      try {
        if (this.isEdit) {
          await axios.put(`/training/courses/${this.courseId}`, this.form);
          this.toast.success('Course updated.');
          this.$router.push(this.$url('/hr/training/courses'));
        } else {
          const { data } = await axios.post('/training/courses', this.form);
          this.toast.success('Course created. You can now add content.');
          // Redirect to edit page so user can add lessons immediately
          this.$router.push(this.$url(`/hr/training/courses/${data.data.id}`));
        }
      } catch (e) {
        this.errorMsg = e.response?.data?.message ?? 'An error occurred.';
      } finally {
        this.saving = false;
      }
    },

    // ── Sections ────────────────────────────────────────────────────────────

    async addSection() {
      this.sectionForm.saving = true;
      try {
        const { data } = await axios.post(`/training/courses/${this.courseId}/sections`, {
          title: 'New Section',
          sort_order: this.curriculum.sections.length,
        });
        this.curriculum.sections.push({
          ...data.data,
          lessons: [],
          _editing: true,
          _titleDraft: data.data.title,
        });
        this.$nextTick(() => {
          const refs = this.$refs.sectionTitleInputs;
          if (refs) {
            const arr = Array.isArray(refs) ? refs : [refs];
            arr[arr.length - 1]?.focus();
          }
        });
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to add section.');
      } finally {
        this.sectionForm.saving = false;
      }
    },

    editSectionTitle(section) {
      section._titleDraft = section.title;
      section._editing = true;
      this.$nextTick(() => {
        const refs = this.$refs.sectionTitleInputs;
        if (refs) {
          const arr = Array.isArray(refs) ? refs : [refs];
          const idx = this.curriculum.sections.indexOf(section);
          arr[idx]?.focus();
        }
      });
    },

    async saveSection(section) {
      if (!section._editing) return;
      section._editing = false;
      const newTitle = section._titleDraft?.trim();
      if (!newTitle || newTitle === section.title) {
        section._titleDraft = section.title;
        return;
      }
      try {
        await axios.put(`/training/courses/${this.courseId}/sections/${section.id}`, { title: newTitle });
        section.title = newTitle;
      } catch (e) {
        section._titleDraft = section.title;
        this.toast.error('Failed to rename section.');
      }
    },

    showDeleteSection(section) {
      this.currConfirm = {
        show: true,
        title: 'Delete Section',
        message: `Delete "${section.title}"? All lessons in this section will also be deleted.`,
        loading: false,
        onOk: async () => {
          await axios.delete(`/training/courses/${this.courseId}/sections/${section.id}`);
          this.curriculum.sections = this.curriculum.sections.filter(s => s.id !== section.id);
        },
      };
    },

    // ── Lessons ─────────────────────────────────────────────────────────────

    lessonTypeLabel(type) {
      const map = {
        video: 'Video', pdf: 'PDF', audio: 'Audio', link: 'Link',
        text: 'Text', doc: 'Doc', scorm: 'SCORM', xapi: 'xAPI',
        quiz: 'Quiz', ack: 'ACK', classroom: 'Class',
      };
      return map[type] || type;
    },

    openLessonModal(sectionId, lesson = null) {
      this.lessonModal.sectionId = sectionId;
      this.lessonModal.editLesson = lesson;
      this.lessonModal.errorMsg = '';
      if (lesson) {
        this.lessonModal.form = {
          title: lesson.title,
          lesson_type: lesson.lesson_type,
          external_url: lesson.external_url || '',
          is_mandatory: lesson.is_mandatory ? 1 : 0,
          duration_minutes: lesson.duration_seconds ? Math.round(lesson.duration_seconds / 60) : '',
        };
      } else {
        this.lessonModal.form = {
          title: '', lesson_type: 'video', external_url: '', is_mandatory: 1, duration_minutes: '',
        };
      }
      this.lessonModal.show = true;
    },

    async saveLessonModal() {
      const f = this.lessonModal.form;
      if (!f.title.trim()) {
        this.lessonModal.errorMsg = 'Lesson title is required.';
        return;
      }
      this.lessonModal.errorMsg = '';
      this.lessonModal.saving = true;

      const payload = {
        title: f.title.trim(),
        lesson_type: f.lesson_type,
        external_url: f.external_url || null,
        is_mandatory: f.is_mandatory,
        duration_seconds: f.duration_minutes ? Math.round(Number(f.duration_minutes) * 60) : null,
        section_id: this.lessonModal.sectionId || null,
      };

      try {
        if (this.lessonModal.editLesson) {
          const { data } = await axios.put(
            `/training/courses/${this.courseId}/lessons/${this.lessonModal.editLesson.id}`,
            payload,
          );
          const updated = data.data;
          if (this.lessonModal.sectionId) {
            const sec = this.curriculum.sections.find(s => s.id === this.lessonModal.sectionId);
            if (sec) {
              const idx = sec.lessons.findIndex(l => l.id === updated.id);
              if (idx !== -1) sec.lessons.splice(idx, 1, updated);
            }
          } else {
            const idx = this.curriculum.flatLessons.findIndex(l => l.id === updated.id);
            if (idx !== -1) this.curriculum.flatLessons.splice(idx, 1, updated);
          }
          this.toast.success('Lesson updated.');
        } else {
          const { data } = await axios.post(`/training/courses/${this.courseId}/lessons`, payload);
          const newLesson = data.data;
          if (this.lessonModal.sectionId) {
            const sec = this.curriculum.sections.find(s => s.id === this.lessonModal.sectionId);
            if (sec) sec.lessons.push(newLesson);
          } else {
            this.curriculum.flatLessons.push(newLesson);
          }
          this.toast.success('Lesson added.');
        }
        this.lessonModal.show = false;
      } catch (e) {
        this.lessonModal.errorMsg = e.response?.data?.message ?? 'Failed to save lesson.';
      } finally {
        this.lessonModal.saving = false;
      }
    },

    showDeleteLesson(lesson, section) {
      this.currConfirm = {
        show: true,
        title: 'Delete Lesson',
        message: `Delete "${lesson.title}"? This action cannot be undone.`,
        loading: false,
        onOk: async () => {
          await axios.delete(`/training/courses/${this.courseId}/lessons/${lesson.id}`);
          if (section) {
            section.lessons = section.lessons.filter(l => l.id !== lesson.id);
          } else {
            this.curriculum.flatLessons = this.curriculum.flatLessons.filter(l => l.id !== lesson.id);
          }
        },
      };
    },

    async handleCurrConfirm() {
      if (!this.currConfirm.onOk) return;
      this.currConfirm.loading = true;
      try {
        await this.currConfirm.onOk();
        this.currConfirm.show = false;
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Action failed.');
        this.currConfirm.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.course-form { padding-bottom: 48px; }
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
.mt-4 { margin-top:20px; }
.mb-4 { margin-bottom:20px; }
.card-section-title { font-size:13px; font-weight:700; color:#1a2b45; text-transform:uppercase; letter-spacing:.05em; margin-bottom:18px; padding-bottom:10px; border-bottom:1px solid #f0f4f8; }
.mb-0 { margin-bottom:0; padding-bottom:0; border-bottom:none; }

.field-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.field-group { display:flex; flex-direction:column; gap:6px; }
.field-group--full { grid-column:1/-1; }
.field-label { font-size:13px; font-weight:600; color:#1a2b45; }
.req { color:#e03535; }
.field-input { padding:9px 12px; border:1.5px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; width:100%; box-sizing:border-box; }
.field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.field-textarea { resize:vertical; min-height:80px; }

.checkbox-group { display:flex; flex-direction:column; justify-content:flex-end; gap:10px; padding-bottom:2px; }
.check-label { display:flex; align-items:center; gap:8px; font-size:13px; color:#4a5e78; cursor:pointer; }
.check-input { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; }

.error-banner { display:flex; align-items:center; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:16px; color:#e03535; font-size:13px; }

.form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:20px; padding-top:20px; border-top:1px solid #f0f4f8; }
.btn-primary { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; border:none; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#0056d2; }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1.5px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; }
.btn-outline:hover { background:#f4f7fb; }

/* ── Curriculum ────────────────────────────────────────────────────────── */

.curriculum-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; padding-bottom:14px; border-bottom:1px solid #f0f4f8; }

.structure-toggle { display:flex; background:#f4f7fb; border-radius:8px; padding:3px; gap:2px; }
.struct-btn { padding:6px 14px; border:none; background:transparent; color:#7a90b0; font-size:12px; font-weight:600; border-radius:6px; cursor:pointer; transition:all .15s; }
.struct-btn.active { background:#fff; color:#004AAD; box-shadow:0 1px 4px rgba(0,0,0,.08); }

.curriculum-loading { display:flex; align-items:center; justify-content:center; padding:40px; }

.curriculum-empty { display:flex; align-items:center; gap:10px; color:#a0b0c8; font-size:13px; padding:24px 0; }
.empty-icon { font-size:20px; opacity:.5; }

.section-block { margin-bottom:12px; border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }

.section-header { display:flex; align-items:center; gap:10px; background:#f8fafc; padding:12px 16px; border-bottom:1px solid #e4eaf2; }
.section-num { font-size:11px; font-weight:700; color:#a0b0c8; text-transform:uppercase; letter-spacing:.05em; flex-shrink:0; }
.section-title-text { flex:1; font-size:13px; font-weight:600; color:#1a2b45; cursor:pointer; }
.section-title-text:hover { color:#004AAD; }
.section-title-input { flex:1; font-size:13px; font-weight:600; color:#1a2b45; border:1.5px solid #004AAD; border-radius:6px; padding:5px 10px; background:#fff; outline:none; }
.section-header-actions { display:flex; gap:4px; margin-left:auto; }

.section-body { padding:12px 16px; display:flex; flex-direction:column; gap:4px; }
.section-empty { font-size:12px; color:#a0b0c8; padding:8px 0; }

.lesson-row { display:flex; align-items:center; gap:10px; padding:9px 12px; background:#f8fafc; border-radius:8px; border:1px solid #eef2f8; }
.lesson-row--indented { background:#fff; }
.lesson-pos { font-size:11px; color:#a0b0c8; font-weight:600; width:18px; text-align:center; flex-shrink:0; }
.lesson-title-cell { flex:1; font-size:13px; color:#1a2b45; font-weight:500; }

.type-badge { padding:2px 8px; border-radius:4px; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.03em; flex-shrink:0; background:#e8eeff; color:#004AAD; }
.type-video  { background:#e8f4ff; color:#0070d0; }
.type-pdf    { background:#fff0f0; color:#d03535; }
.type-audio  { background:#f0f8ee; color:#2d8a3e; }
.type-link   { background:#fff8e8; color:#b07000; }
.type-text   { background:#f5f0ff; color:#6040c0; }
.type-doc    { background:#edf7f5; color:#1a7a6a; }
.type-quiz   { background:#fff0f8; color:#b03080; }

.row-actions { display:flex; gap:4px; margin-left:auto; }
.row-btn { width:28px; height:28px; border:none; background:transparent; color:#a0b0c8; border-radius:6px; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:12px; transition:all .15s; }
.row-btn:hover { background:#f0f4f8; color:#1a2b45; }
.row-btn--danger:hover { background:#fff0f0; color:#e03535; }

.btn-add-item { display:inline-flex; align-items:center; gap:7px; margin-top:12px; padding:8px 16px; border:1.5px dashed #c2d0e8; border-radius:8px; background:#f8fafc; color:#5a7aaa; font-size:13px; font-weight:500; cursor:pointer; transition:all .15s; }
.btn-add-item:hover:not(:disabled) { border-color:#004AAD; color:#004AAD; background:#f0f4ff; }
.btn-add-item:disabled { opacity:.5; cursor:not-allowed; }
.btn-add-item--sm { margin-top:8px; padding:6px 12px; font-size:12px; }

.curriculum-hint { display:flex; align-items:center; gap:12px; color:#7a90b0; font-size:13px; padding:18px 24px; }

/* ── Modals ─────────────────────────────────────────────────────────────── */

.modal-backdrop { position:fixed; inset:0; background:rgba(10,20,40,.45); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; }

.modal-box { background:#fff; border-radius:14px; width:100%; max-width:480px; overflow:hidden; box-shadow:0 20px 60px rgba(0,0,0,.18); }
.modal-head { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f4f8; }
.modal-title { font-size:15px; font-weight:700; color:#1a2b45; }
.modal-close { border:none; background:none; color:#a0b0c8; cursor:pointer; font-size:16px; padding:4px 8px; border-radius:6px; line-height:1; }
.modal-close:hover { background:#f4f7fb; color:#1a2b45; }
.modal-body-inner { padding:20px; }
.modal-foot { display:flex; align-items:center; justify-content:flex-end; gap:10px; padding:14px 20px; border-top:1px solid #f0f4f8; }

.field-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.mb-3 { margin-bottom:12px; }

/* ── Confirm ────────────────────────────────────────────────────────────── */

.confirm-box { background:#fff; border-radius:16px; padding:28px; width:100%; max-width:380px; text-align:center; box-shadow:0 20px 60px rgba(0,0,0,.18); }
.confirm-icon-wrap { width:52px; height:52px; border-radius:50%; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; font-size:20px; margin:0 auto 14px; }
.confirm-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.confirm-msg { font-size:13px; color:#7a90b0; margin:0 0 20px; line-height:1.5; }
.confirm-actions { display:flex; gap:10px; justify-content:center; }
.btn-confirm-delete { display:inline-flex; align-items:center; gap:6px; background:#e03535; color:#fff; border:none; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; }
.btn-confirm-delete:hover:not(:disabled) { background:#c02828; }
.btn-confirm-delete:disabled { opacity:.6; cursor:not-allowed; }

/* ── Loader ─────────────────────────────────────────────────────────────── */

.full-loader { display:flex; align-items:center; justify-content:center; min-height:300px; }
.spinner { width:32px; height:32px; border:3px solid #e4eaf2; border-top-color:#004AAD; border-radius:50%; animation:spin .7s linear infinite; }
.spinner-sm { width:14px; height:14px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; display:inline-block; }
.spinner-sm--dark { border:2px solid #c2d0e8; border-top-color:#004AAD; }
@keyframes spin { to { transform:rotate(360deg); } }

@media(max-width:600px) {
  .field-grid { grid-template-columns:1fr; }
  .field-group--full { grid-column:auto; }
  .field-grid-2 { grid-template-columns:1fr; }
  .curriculum-header { flex-direction:column; align-items:flex-start; }
}
</style>
